import React, { useEffect, useState, useRef, useCallback } from 'react'
import { find, pick, mapValues, isNil, some } from 'lodash'
import I18n from 'i18n'
import { Link } from 'react-router-dom'

import SelectDropdown from '../../../common/SelectDropdown'
import { WithSession } from '../../../session/SessionProvider'
import DashboardMulti from '../../layout/DashboardMulti'
import CoachHomeTab from './CoachHomeTab'
import CoachLoadTab from './CoachLoadTab'
import { WithBackend } from '../../../backend/BackendProvider'
import { COACH_DASHBOARD_TITLES, WELLBEING_SCORE, DAILY_QUESTIONNAIRE_METRICS, COACH_DASHBOARD_LOAD_TITLES, groupTypes, subscriptionTypes } from '../../../common/Constants'
import CoachTrainingTab from './CoachTrainingTab'
import {
  fetchUserData, addFirstDatasetOfEachTypePerProfile
} from './CoachDashboardHelper'
import { getGroupAcceptedMembers, getGroupAcceptedMembersNotAdmin, isCoachOfGroup } from '../../../common/utils/GroupUtils'
import PremiumOverlay from 'components/premium_membership/PremiumOverlay'
import CoachInControlLogo from '../../../../../../public/images/logo_coachincontrol.svg'
import { WithAppsignal } from 'components/appsignal/AppsignalProvider'
import { WithFlashMessages } from 'components/flashmessages/FlashMessageProvider'
import TabPanel, { Tab } from 'components/atomic/atoms/TabPanel'
import { TrainingSchedulesProvider } from '../../../training/context/TrainingSchedulesContext'
import moment from 'moment'

// TODO: sometimes it doesn't load the data for a day, look into why this is.
const getAllAthleteDataOnDay = (backend, groupId, day) => {
  const beginningOfDay = moment(day).startOf('day')
  const endOfDay = moment(day).endOf('day')
  const data = {}

  return fetchUserData(backend, groupId, undefined, beginningOfDay, endOfDay).then((datasets) => {
    addFirstDatasetOfEachTypePerProfile(data, datasets)
    return Promise.resolve(data)
  })
}

// If no recent data at all, no need to fetch historical.
// Show the no data prompt.
const hasAnyRecentData = (recentObj) => {
  return some(recentObj, dataset => some(dataset, val => !isNil(val)))
}

const CoachDashboardIndividual = (props) => {
  const [groups, setGroups] = useState([])
  const [groupRecentData, setGroupRecentData] = useState([])
  const [selectedGroup, setSelectedGroup] = useState({})
  const [selectedAthlete, setSelectedAthlete] = useState(undefined)
  const [selectedTab, setSelectedTab] = useState('home')
  // Sets to midnight once and updates in a single call for the data to
  // not be fetched multiple times for different times of the same day.
  const [homeTabDay, setHomeTabDay] = useState(() => {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    return d
  })
  const [state, setState] = useState('isFetching')
  const initialCoachAdminState = () => {
    const toggleState = JSON.parse(window.localStorage.getItem('coachAdminToggle'))
    if (typeof toggleState === 'boolean') {
      return toggleState
    }
    window.localStorage.setItem('coachAdminToggle', JSON.stringify(true))
    return true
  }
  const [excludeAdmins, setExcludeAdmins] = useState(initialCoachAdminState)

  const { myProfile, backend, appsignal, flashMessages } = props
  const myProfileId = myProfile?.id
  const groupId = selectedGroup?.id

  const requestSeq = useRef(0)

  const fetchData = useCallback(async () => {
    if (!groupId || !selectedGroup) return

    const seq = ++requestSeq.current
    setState('isFetching')
    setGroupRecentData({})

    try {
      // 1) Fetch most-recent day data (for display values)
      const recent = await getAllAthleteDataOnDay(backend, groupId, homeTabDay)
      if (seq !== requestSeq.current) return

      const hasRecent = hasAnyRecentData(recent)
      if (!hasRecent) {
        setState('noData')
        return
      }

      const members = (excludeAdmins
        ? getGroupAcceptedMembersNotAdmin(selectedGroup.group_memberships)
        : getGroupAcceptedMembers(selectedGroup.group_memberships)
      )

      // Show data immediately without z-scores while the backend computes them
      const dayDataWithoutZScores = {}
      members.forEach((profile) => {
        const chosenDayData = recent[profile.id]
        if (chosenDayData) {
          dayDataWithoutZScores[profile.id] = { datasets: chosenDayData, profile }
        }
      })
      setGroupRecentData(dayDataWithoutZScores)
      setState('zScoring')

      // 2) Single backend call: fetch pre-computed z-scores + ACWR for all athletes
      const dayStr = moment(homeTabDay).format('YYYY-MM-DD')
      let zScoreData = {}
      try {
        const zScoreResponse = await backend.dashboards.coachDashboard.zScores(groupId, dayStr)
        if (seq !== requestSeq.current) return
        zScoreData = zScoreResponse?.data?.z_scores || {}
      } catch (zScoreErr) {
        // If z-score endpoint fails, fall back to showing data without z-scores
        console.warn('Z-score computation failed, showing data without z-scores:', zScoreErr)
        if (flashMessages) {
          flashMessages.push(
            I18n.t('flashmessages.general.error_retrieving_data'),
            flashMessages.duration.SHORT,
            flashMessages.levels.WARNING
          )
        }
      }

      // 3) Merge pre-computed z-scores into athlete datasets
      const tmpGroupRecentData = {}
      members.forEach((profile) => {
        const chosenDayData = recent[profile.id] ? recent[profile.id] : {}
        // Attach pre-computed z-scores from the backend
        chosenDayData.preComputedZScores = zScoreData[profile.id] || {}
        // Attach ACWR value from the backend z-score response
        const acwrData = zScoreData[profile.id]?.acwr
        if (acwrData?.value != null) {
          chosenDayData.extraData = { acwr: acwrData.value }
        }
        tmpGroupRecentData[profile.id] = { datasets: chosenDayData, profile }
      })

      if (seq !== requestSeq.current) return
      setGroupRecentData(tmpGroupRecentData)
      setState('ready')
    } catch (err) {
      if (seq !== requestSeq.current) return
      setState('error')
      appsignal?.sendError?.(err)
    }
  }, [backend, groupId, homeTabDay, selectedGroup, excludeAdmins, appsignal, flashMessages])

  useEffect(() => {
    if (!!myProfileId || myProfileId === 0) {
      backend.groups.get().then((fetchedGroups) => {
        const groups = fetchedGroups.data.filter((group) => [groupTypes.GROUP, groupTypes.ANONYMIZED].includes(group.group_type) && isCoachOfGroup(group.group_memberships, myProfileId))
        setGroups(groups)
      })
    }
  }, [myProfileId])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const onGroupChange = (e) => {
    const newGroupId = parseInt(e.target.value)
    const newGroup = find(groups, (group) => group.id === newGroupId)
    setSelectedGroup(newGroup)
    setSelectedAthlete(undefined)
  }

  const onSelectedAthleteChange = (e) => {
    const newAthleteId = parseInt(e.target.value)
    if (newAthleteId) {
      const newAthlete = find(selectedGroup.group_memberships, (membership) => membership.profile.id === newAthleteId)
      setSelectedAthlete(newAthlete?.profile)
    }
  }

  const onAthleteClick = (athlete) => {
    setSelectedTab('training')
    setSelectedAthlete(athlete)
  }

  const handleHomeTabDayChange = (e) => {
    const selectedDate = new Date(e)
    selectedDate.setHours(0, 0, 0, 0)
    setHomeTabDay(selectedDate)
  }

  const setExcludeAdminsPersistent = () => {
    const next = !excludeAdmins
    window.localStorage.setItem('coachAdminToggle', JSON.stringify(next))
    setExcludeAdmins(next)
  }
  const groupDropdownOptions = groups.map((group) => {
    const groupOption = pick(group, ['name', 'id'])
    groupOption.value = groupOption.id
    return groupOption
  })

  const atheleteDropdownOptions = selectedGroup?.group_memberships?.map((membership) => (
    {
      name: membership.profile.first_name + ' ' + membership.profile.last_name,
      value: membership.profile.id,
      icon: membership.profile.picture
    }
  )) || []

  const titles = mapValues(COACH_DASHBOARD_TITLES || {}, (translationPath) => I18n.t(translationPath))
  const loadTitles = mapValues(COACH_DASHBOARD_LOAD_TITLES || {}, (translationPath) => I18n.t(translationPath))
  const questionnaireMetrics = mapValues(DAILY_QUESTIONNAIRE_METRICS || {}, (translationPath) => I18n.t(translationPath))
  const wellbeingScore = mapValues(WELLBEING_SCORE || {}, (translationPath) => I18n.t(translationPath))
  const logos = [
    <Link to={{ pathname: 'https://innovatielab.thialf.nl/projecten/coach-in-control' }} key='cic' target='_blank'>
      <img alt='Coach in Control' src={CoachInControlLogo} className='coach-in-control-logo' />
    </Link>
  ]

  return (
    <PremiumOverlay activeFor={[subscriptionTypes.coach, subscriptionTypes.researcher]} showBadge={false}>
      <DashboardMulti
        title={I18n.t('components.dashboards.coach_individual.title')}
        logos={logos}
        ready
      >
        <div className='row'>
          <div className='col s12'>
            <SelectDropdown
              content={groupDropdownOptions}
              placeholder={I18n.t('components.dashboards.select_dropdown.group')}
              id='select-group-dropdown'
              label={I18n.t('components.dashboards.select_dropdown.group')}
              onChange={onGroupChange}
            />
          </div>
        </div>
        <TrainingSchedulesProvider>
          <TabPanel id='coach-dashboard-tabs' onShow={setSelectedTab} selectedTab={selectedTab}>
            <Tab id='home' title={I18n.t('components.dashboards.coach_individual.home.title')}>
              <CoachHomeTab
                athletesEntryData={groupRecentData} titles={titles}
                day={homeTabDay} onDayChange={handleHomeTabDayChange}
                onAthleteClick={onAthleteClick} excludeAdmins={excludeAdmins} setExcludeAdmins={setExcludeAdminsPersistent}
                state={state}
                groupId={groupId}
                selectedGroup={selectedGroup}
              />
            </Tab>
            <Tab id='training' title={I18n.t('components.dashboards.coach_individual.training.title')}>
              {selectedTab === 'training' && (
                <>
                  <SelectDropdown
                    content={atheleteDropdownOptions}
                    placeholder={I18n.t('components.dashboards.select_dropdown.athlete')}
                    id='select-athlete-dropdown'
                    label={I18n.t('components.dashboards.select_dropdown.athlete')}
                    onChange={onSelectedAthleteChange}
                    defaultValue={selectedAthlete?.id}
                    value={selectedAthlete?.id}
                  />
                  <CoachTrainingTab athleteProfile={selectedAthlete} titles={titles} groupId={groupId} selectedHomeTabDate={homeTabDay} />
                </>
              )}
            </Tab>
            <Tab id='load' title={I18n.t('components.dashboards.coach_individual.load.title')}>
              {/* TODO: remove the not-rendering of the tabs after we remove the loading of the data from training and load tab, and just pass it down from this component. */}
              {selectedTab === 'load' && (
                <>
                  <SelectDropdown
                    content={atheleteDropdownOptions}
                    placeholder={I18n.t('components.dashboards.select_dropdown.athlete')}
                    id='select-athlete-dropdown-load'
                    label={I18n.t('components.dashboards.select_dropdown.athlete')}
                    onChange={onSelectedAthleteChange}
                    defaultValue={selectedAthlete?.id}
                    value={selectedAthlete?.id}
                  />
                  <CoachLoadTab onAthleteClick={onAthleteClick} athletesEntryData={groupRecentData} questionnaireMetrics={questionnaireMetrics} wellbeingScore={wellbeingScore} athleteProfile={selectedAthlete} titles={titles} loadTitles={loadTitles} groupId={groupId} selectedHomeTabDate={homeTabDay} />
                </>)}
            </Tab>
          </TabPanel>
        </TrainingSchedulesProvider>
      </DashboardMulti>
    </PremiumOverlay>
  )
}

export default WithFlashMessages(WithAppsignal(WithBackend(WithSession(CoachDashboardIndividual))))
